import { supabase } from "@/integrations/supabase/client";
import { Agent } from "@/types/agent/agent";
import {
  AgentEvaluationConfig,
  GenerateEvaluationTestsResponse,
  SuggestedTestCategory,
} from "@/types/agent/brand-integrity";

export class EvaluationSuggestionService {
  /**
   * Generate a hash of the agent config for change detection
   */
  static generateConfigHash(agent: Agent): string {
    const config = this.extractAgentConfig(agent);
    const content = JSON.stringify({
      prompt: config.prompt || "",
      goal: config.goal || "",
      category: config.category || "",
      firstMessage: config.firstMessage || "",
    });

    // Simple hash function (matches edge function)
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  /**
   * Extract evaluation-relevant config from an agent
   */
  static extractAgentConfig(agent: Agent): AgentEvaluationConfig {
    const settings = agent.settings as any;
    const conversationConfig = settings?.conversation_config;
    const agentPrompt = conversationConfig?.agent?.prompt;

    return {
      prompt: typeof agentPrompt === "string" ? agentPrompt : agentPrompt?.prompt || "",
      goal: agent.insights?.description || "",
      category: agent.category || "",
      firstMessage: conversationConfig?.agent?.first_message || "",
      agentName: agent.name,
    };
  }

  /**
   * Get suggested tests for an agent (cached or generated)
   */
  static async getSuggestedTests(agent: Agent): Promise<{
    suggestions: SuggestedTestCategory[];
    configHash: string;
    cached: boolean;
    generatedAt: string | null;
  }> {
    try {
      const agentConfig = this.extractAgentConfig(agent);

      const { data, error } = await supabase.functions.invoke("genie-integrity-audit", {
        body: {
          action: "generate-custom-tests",
          agentId: agent.id,
          agentConfig,
          forceRegenerate: false,
        },
      });

      if (error) {
        console.error("Error fetching suggestions:", error);
        throw error;
      }

      const response = data as GenerateEvaluationTestsResponse;

      return {
        suggestions: response.suggestedTests || [],
        configHash: response.configHash,
        cached: response.cached,
        generatedAt: response.generatedAt,
      };
    } catch (error) {
      console.error("Error getting suggested tests:", error);
      // Return empty on error so UI can still function
      return {
        suggestions: [],
        configHash: "",
        cached: false,
        generatedAt: null,
      };
    }
  }

  /**
   * Force regeneration of suggestions (ignores cache)
   */
  static async regenerateSuggestions(agent: Agent): Promise<{
    suggestions: SuggestedTestCategory[];
    configHash: string;
  }> {
    try {
      const agentConfig = this.extractAgentConfig(agent);

      const { data, error } = await supabase.functions.invoke("genie-integrity-audit", {
        body: {
          action: "generate-custom-tests",
          agentId: agent.id,
          agentConfig,
          forceRegenerate: true,
        },
      });

      if (error) {
        console.error("Error regenerating suggestions:", error);
        throw error;
      }

      const response = data as GenerateEvaluationTestsResponse;

      return {
        suggestions: response.suggestedTests || [],
        configHash: response.configHash,
      };
    } catch (error) {
      console.error("Error regenerating suggestions:", error);
      throw error;
    }
  }

  /**
   * Check if cached suggestions are stale (config has changed)
   */
  static async isCacheStale(agentId: string, currentConfigHash: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("agent_evaluation_suggestions")
        .select("config_hash")
        .eq("agent_id", agentId)
        .single();

      if (error || !data) {
        // No cache exists, so it's "stale" (needs generation)
        return true;
      }

      return data.config_hash !== currentConfigHash;
    } catch (error) {
      console.error("Error checking cache staleness:", error);
      return true;
    }
  }

  /**
   * Get cached suggestions directly from database (for quick initial load)
   */
  static async getCachedSuggestions(agentId: string): Promise<SuggestedTestCategory[] | null> {
    try {
      const { data, error } = await supabase
        .from("agent_evaluation_suggestions")
        .select("suggested_tests")
        .eq("agent_id", agentId)
        .single();

      if (error || !data) {
        return null;
      }

      return data.suggested_tests as SuggestedTestCategory[];
    } catch (error) {
      console.error("Error fetching cached suggestions:", error);
      return null;
    }
  }

  /**
   * Clear cached suggestions for an agent
   */
  static async clearCache(agentId: string): Promise<void> {
    try {
      await supabase.from("agent_evaluation_suggestions").delete().eq("agent_id", agentId);
    } catch (error) {
      console.error("Error clearing suggestion cache:", error);
    }
  }
}
